// Construction Loan Calculator Logic
// Based on SU sheet calculations - Cell C36 = C17 + C27

export const CLOSING_COST_RATE = 0.045; // Fixed at 4.5% of loan amount

/**
 * Calculate Total Project Cost
 * = Acquisition/Value of Land + Hard Costs + Soft Costs + Financing Costs
 */
export const calculateTotalProjectCost = (inputs) => {
  const acquisitionCost = parseFloat(inputs.acquisitionCost) || 0;
  const hardCosts = parseFloat(inputs.hardCosts) || 0;
  const softCosts = parseFloat(inputs.softCosts) || 0;
  const financingCosts = parseFloat(inputs.financingCosts) || 0;
  
  return acquisitionCost + hardCosts + softCosts + financingCosts;
};

/**
 * Calculate Max As-Is Loan
 * = As-Is Appraised Value × Target LTV
 */
export const calculateMaxAsIsLoan = (asIsValue, targetLTV) => {
  const value = parseFloat(asIsValue) || 0;
  const ltv = parseFloat(targetLTV) / 100 || 0;
  
  return value * ltv;
};

/**
 * Calculate Max LTC Loan (Total)
 * = lesser of: (As-Completed Value × Target LTV) OR (Target LTC × Total Project Cost)
 */
export const calculateMaxLTCLoan = (asCompletedValue, targetLTV, targetLTC, totalProjectCost) => {
  const completedValue = parseFloat(asCompletedValue) || 0;
  const ltv = parseFloat(targetLTV) / 100 || 0;
  const ltc = parseFloat(targetLTC) / 100 || 0;
  
  const ltvBasedLoan = completedValue * ltv;
  const ltcBasedLoan = totalProjectCost * ltc;
  
  return Math.min(ltvBasedLoan, ltcBasedLoan);
};

/**
 * Calculate Initial Funding (SU Sheet C17)
 * = Acquisition Cost - Equity on Purchase (constrained by Max As-Is Loan)
 */
export const calculateInitialFunding = (inputs, maxAsIsLoan) => {
  const acquisitionCost = parseFloat(inputs.acquisitionCost) || 0;
  const equityOnPurchase = parseFloat(inputs.equityOnPurchase) || 0;
  
  const acquisitionLoanNeeded = Math.max(acquisitionCost - equityOnPurchase, 0);
  return Math.min(acquisitionLoanNeeded, maxAsIsLoan);
};

/**
 * Calculate Construction Funding (SU Sheet C27)
 * = Hard Costs + Soft Costs (constrained by remaining LTC capacity)
 */
export const calculateConstructionFunding = (inputs, initialFunding, maxLTCLoan) => {
  const hardCosts = parseFloat(inputs.hardCosts) || 0;
  const softCosts = parseFloat(inputs.softCosts) || 0;
  
  const constructionCosts = hardCosts + softCosts;
  const remainingLTCCapacity = Math.max(maxLTCLoan - initialFunding, 0);
  
  return Math.min(constructionCosts, remainingLTCCapacity);
};

/**
 * Calculate Final Loan Amount (SU Sheet C36)
 * = Initial Funding + Construction Funding
 */
export const calculateFinalLoanAmount = (initialFunding, constructionFunding) => {
  return initialFunding + constructionFunding;
};

/**
 * Calculate Resulting LTV
 * = Final Loan Amount / As-Completed Appraised Value
 */
export const calculateResultingLTV = (finalLoanAmount, asCompletedValue) => {
  const completedValue = parseFloat(asCompletedValue) || 0;
  if (completedValue === 0) return 0;
  return (finalLoanAmount / completedValue) * 100;
};

/**
 * Calculate Closing Costs
 * = 4.5% of Loan Amount
 */
export const calculateClosingCosts = (loanAmount) => {
  return loanAmount * CLOSING_COST_RATE;
};

/**
 * Calculate Interest Reserve
 * = Loan Amount × Interest Rate × (Months ÷ 12)
 */
export const calculateInterestReserve = (loanAmount, interestRate, reserveMonths) => {
  const rate = parseFloat(interestRate) / 100 || 0;
  const months = parseFloat(reserveMonths) || 0;
  return Math.round(loanAmount * rate * (months / 12));
};

/**
 * Calculate Total Reserves/Fees
 * = Property Tax Reserve + Inspection Fees + Additional Holdback
 */
export const calculateTotalReserves = (inputs) => {
  const taxReserve = parseFloat(inputs.propertyTaxReserve) || 0;
  const inspectionFees = parseFloat(inputs.inspectionFees) || 0;
  const additionalHoldback = parseFloat(inputs.additionalHoldback) || 0;
  return taxReserve + inspectionFees + additionalHoldback;
};

/**
 * Main calculation function that returns all metrics
 * Follows SU sheet logic: Final Loan = Initial Funding + Construction Funding
 */
export const calculateAllMetrics = (inputs) => {
  // Parse all inputs
  const acquisitionCost = parseFloat(inputs.acquisitionCost) || 0;
  const hardCosts = parseFloat(inputs.hardCosts) || 0;
  const softCosts = parseFloat(inputs.softCosts) || 0;
  const financingCosts = parseFloat(inputs.financingCosts) || 0;
  const existingDebt = parseFloat(inputs.existingDebt) || 0;
  const equityOnPurchase = parseFloat(inputs.equityOnPurchase) || 0;
  const spentToDate = parseFloat(inputs.spentToDate) || 0;
  const additionalHoldback = parseFloat(inputs.additionalHoldback) || 0;
  
  // Step 1: Calculate Total Project Cost
  const totalProjectCost = calculateTotalProjectCost(inputs);
  
  // Step 2: Calculate loan limits
  const maxAsIsLoan = calculateMaxAsIsLoan(inputs.asIsValue, inputs.targetLTV);
  const maxLTCLoan = calculateMaxLTCLoan(
    inputs.asCompletedValue,
    inputs.targetLTV,
    inputs.targetLTC,
    totalProjectCost
  );
  
  // Step 3: Calculate Initial Funding (constrained by As-Is LTV)
  const initialFunding = calculateInitialFunding(inputs, maxAsIsLoan);
  
  // Step 4: Calculate Construction Funding (constrained by remaining LTC capacity)
  const constructionFunding = calculateConstructionFunding(inputs, initialFunding, maxLTCLoan);
  
  // Step 5: Final Loan Amount = Initial Funding + Construction Funding
  const finalLoanAmount = calculateFinalLoanAmount(initialFunding, constructionFunding);
  
  // Step 6: Calculate resulting LTV
  const resultingLTV = calculateResultingLTV(finalLoanAmount, inputs.asCompletedValue);
  
  // Step 7: Calculate costs based on final loan amount
  const closingCosts = calculateClosingCosts(finalLoanAmount);
  const totalReserves = calculateTotalReserves(inputs);
  
  // Step 8: Calculate interest reserve
  const interestReserveMonths = parseFloat(inputs.interestReserveMonths) || 0;
  const interestReserve = calculateInterestReserve(
    finalLoanAmount,
    inputs.interestRate,
    interestReserveMonths
  );
  
  // Step 9: Calculate Construction Costs for Uses using Excel SU sheet formula:
  // Construction Costs = I8 + I9 − (I12 − M7 − M8)
  // Where:
  //   I8 = Hard Costs
  //   I9 = Soft Costs
  //   I12 = Spent to Date
  //   M7 = Equity on Purchase
  //   M8 = Existing Debt
  //
  // Formula: Construction Costs = Hard + Soft - (Spent to Date - Equity on Purchase - Existing Debt)
  // This reduces construction costs by the NET capital already applied
  const netCapitalApplied = spentToDate - equityOnPurchase - existingDebt;
  const constructionCosts = Math.max(hardCosts + softCosts - netCapitalApplied, 0);
  
  // Step 10: Calculate Total Uses
  // Total Uses = Construction Costs + Existing Debt + Closing Costs + Interest Reserve + Reserves
  const totalUses = constructionCosts + existingDebt + closingCosts + interestReserve + totalReserves;
  
  // Step 11: Calculate Equity Requirement
  // Equity = Total Uses - Loan (if positive, otherwise 0)
  const equityRequirement = Math.max(totalUses - finalLoanAmount, 0);
  
  // Step 12: Calculate Cost Remaining
  // Cost Remaining = Total Project Cost - Spent to Date
  const costRemaining = totalProjectCost - spentToDate;
  
  // Step 13: Total Sources = Total Uses (balanced)
  const totalSources = totalUses;
  
  return {
    // Key metrics
    totalProjectCost,
    maxAsIsLoan,
    maxLTCLoan,
    finalLoanAmount,
    resultingLTV,
    costRemaining,
    
    // Closing and reserves
    closingCosts,
    totalReserves,
    interestReserve,
    
    // Funding breakdown
    initialFunding,
    constructionFunding,
    
    // Construction Costs for Uses (adjusted per Excel formula)
    constructionCosts,
    
    // Existing Debt for Uses
    existingDebt,
    
    // Equity
    equityRequirement,
    
    // Totals (balanced)
    totalSources,
    totalUses,
    
    // Individual inputs for display
    acquisitionCost,
    hardCosts,
    softCosts,
    financingCosts,
    spentToDate,
    equityOnPurchase,
    propertyTaxReserve: parseFloat(inputs.propertyTaxReserve) || 0,
    inspectionFees: parseFloat(inputs.inspectionFees) || 0,
    additionalHoldback,
  };
};
